import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Box,
  Tooltip,
  Divider,
  useScrollTrigger,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  InputAdornment,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  AccountCircleOutlined,
  LogoutOutlined,
  SettingsOutlined,
  GroupOutlined,
  CreditCardOutlined,
  BusinessOutlined,
  PhoneOutlined,
  AccountBalanceOutlined,
  HomeOutlined,
  SaveOutlined,
  CheckCircleOutline,
  LockOutlined,
  MenuOutlined,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { tritonStyles } from '../styles/TritonTheme';
import { getApiBaseUrl } from '../utils/apiConfig';

const API_BASE_URL = getApiBaseUrl();

// Styled Components im Triton-Design
const TritonAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'scrolled',
})(({ theme, scrolled }) => ({
  background: scrolled 
    ? 'rgba(26, 54, 93, 0.95)' 
    : tritonStyles.gradients.primary,
  backdropFilter: scrolled ? 'blur(10px)' : 'none',
  borderBottom: `1px solid ${scrolled ? 'rgba(255, 255, 255, 0.1)' : 'transparent'}`,
  transition: tritonStyles.transitions.standard,
  boxShadow: scrolled 
    ? tritonStyles.elevations.medium 
    : tritonStyles.elevations.subtle,
}));

const LogoSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  cursor: 'pointer',
  padding: theme.spacing(1),
  borderRadius: theme.spacing(1),
  transition: tritonStyles.transitions.standard,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: '#ffffff',
  transition: tritonStyles.transitions.standard,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    transform: 'translateY(-1px)',
  },
}));

const UserSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.spacing(3),
  border: '1px solid rgba(255, 255, 255, 0.2)',
  transition: tritonStyles.transitions.standard,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
}));

const UserMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: theme.spacing(2),
    marginTop: theme.spacing(1),
    minWidth: 240,
    background: 'rgba(255, 255, 255, 0.98)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(226, 232, 240, 0.8)',
    boxShadow: tritonStyles.elevations.high,
  },
}));

const TritonHeader = ({ user, onLogout, onMenuToggle }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const trigger = useScrollTrigger();

  // Profil-Dialog
  const [profilOpen, setProfilOpen] = useState(false);
  const [profilTab, setProfilTab] = useState('stamm'); // 'stamm' | 'passwort'
  const [profilLoading, setProfilLoading] = useState(false);
  const [profilSaving, setProfilSaving] = useState(false);
  const [profilMsg, setProfilMsg] = useState({ text: '', error: false });
  const [stamm, setStamm] = useState({
    firma: '', telefon: '', strasse: '', hausnummer: '',
    plz: '', ort: '', iban: '', bic: '', bank_name: '',
    unterschrift_bild_url: '',
  });
  const [stammBefuellt, setStammBefuellt] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [unterschriftFile, setUnterschriftFile] = useState(null);
  const [unterschriftUploading, setUnterschriftUploading] = useState(false);
  const [pwAlt, setPwAlt] = useState('');
  const [pwNeu, setPwNeu] = useState('');
  const [pwBest, setPwBest] = useState('');

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const closeMenuAndNavigate = (targetPath) => {
    const active = document.activeElement;
    if (active && typeof active.blur === 'function') {
      active.blur();
    }
    setAnchorEl(null);
    window.setTimeout(() => {
      navigate(targetPath);
    }, 0);
  };

  const ladeStammdaten = useCallback(async () => {
    setProfilLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/user/stammdaten`, { credentials: 'include' });
      if (!res.ok) throw new Error('Fehler');
      const s = await res.json();
      setStamm({
        firma: s.firma || '', telefon: s.telefon || '',
        strasse: s.strasse || '', hausnummer: s.hausnummer || '',
        plz: s.plz || '', ort: s.ort || '',
        iban: s.iban || '', bic: s.bic || '', bank_name: s.bank_name || '',
        unterschrift_bild_url: s.unterschrift_bild_url || '',
      });
      setStammBefuellt(!!(s.strasse && s.ort && s.iban));
    } catch {
      setProfilMsg({ text: 'Stammdaten konnten nicht geladen werden', error: true });
    } finally {
      setProfilLoading(false);
    }
  }, []);

  const handleOpenProfil = () => {
    handleMenuClose();
    setProfilMsg({ text: '', error: false });
    setProfilTab('stamm');
    setProfilOpen(true);
    ladeStammdaten();
  };

  const handleUnterschriftUpload = async (file) => {
    if (!file) return;
    setUnterschriftUploading(true);
    try {
      const formData = new FormData();
      formData.append('unterschrift', file);
      const res = await fetch(`${API_BASE_URL}/api/user/unterschrift`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Upload fehlgeschlagen');
      setStamm(s => ({ ...s, unterschrift_bild_url: data.url }));
      setUnterschriftFile(null);
      setProfilMsg({ text: 'Unterschrift hochgeladen', error: false });
    } catch (err) {
      setProfilMsg({ text: err.message, error: true });
    } finally {
      setUnterschriftUploading(false);
    }
  };

  const handleStammSave = async (e) => {
    e.preventDefault();
    setProfilSaving(true);
    setProfilMsg({ text: '', error: false });
    try {
      const res = await fetch(`${API_BASE_URL}/api/user/stammdaten`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(stamm),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Fehler');
      setStammBefuellt(!!(stamm.strasse && stamm.ort && stamm.iban));
      setProfilMsg({ text: 'Stammdaten gespeichert', error: false });
    } catch (err) {
      setProfilMsg({ text: err.message, error: true });
    } finally {
      setProfilSaving(false);
    }
  };

  const handlePasswortSave = async (e) => {
    e.preventDefault();
    if (pwNeu !== pwBest) {
      return setProfilMsg({ text: 'Passwörter stimmen nicht überein', error: true });
    }
    if (pwNeu.length < 6) {
      return setProfilMsg({ text: 'Neues Passwort muss mindestens 6 Zeichen lang sein', error: true });
    }
    setProfilSaving(true);
    setProfilMsg({ text: '', error: false });
    try {
      const res = await fetch(`${API_BASE_URL}/api/user/change-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword: pwAlt, newPassword: pwNeu }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Fehler');
      setPwAlt(''); setPwNeu(''); setPwBest('');
      setProfilMsg({ text: 'Passwort erfolgreich geändert', error: false });
    } catch (err) {
      setProfilMsg({ text: err.message, error: true });
    } finally {
      setProfilSaving(false);
    }
  };


  const handleSettings = () => {
    closeMenuAndNavigate('/einstellungen');
  };

  const handleTeamSettings = () => {
    closeMenuAndNavigate('/mandanten/einladungen');
  };

  const handleBillingSettings = () => {
    closeMenuAndNavigate('/einstellungen/abo');
  };

  const handleLogout = () => {
    // Zuerst das Menü schließen, um aria-hidden Fehler zu vermeiden
    setAnchorEl(null);

    // Kleine Verzögerung, damit das Menü Zeit hat sich zu schließen
    setTimeout(() => {
      if (onLogout) {
        onLogout();
      } else {
        // Fallback, falls kein Handler übergeben wurde
        sessionStorage.clear();
        localStorage.removeItem('user');
        window.location.href = '/';
      }
    }, 100);
  };

  return (
    <>
      <TritonAppBar position="fixed" scrolled={trigger}>
        <Toolbar sx={{ 
          minHeight: { xs: 64, sm: 72 },
          px: { xs: 2, sm: 3 },
          justifyContent: 'space-between'
        }}>
          {/* Logo und Firmenname */}
          <LogoSection onClick={() => window.location.href = '/'}>
            <BusinessOutlined sx={{ fontSize: 32, color: '#d69e2e' }} />
            <Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '1.25rem',
                  lineHeight: 1.2,
                  display: { xs: 'none', sm: 'block' }
                }}
              >
                Immobilienverwaltung
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '0.75rem',
                  display: { xs: 'none', md: 'block' }
                }}
              >
                Professional Property Management
              </Typography>
            </Box>
          </LogoSection>

          {/* Mobile: Benutzer-Menü + Navigations-Menü */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 0.5 }}>
            <StyledIconButton onClick={handleMenuOpen} aria-label="Benutzermenü">
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  background: tritonStyles.gradients.accent,
                  color: '#ffffff',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                }}
              >
                {(user?.name || user?.username || 'U').charAt(0).toUpperCase()}
              </Avatar>
            </StyledIconButton>
            <StyledIconButton onClick={onMenuToggle} aria-label="Navigation öffnen">
              <MenuOutlined />
            </StyledIconButton>
          </Box>

          {/* Desktop Navigation */}
          <Box sx={{ 
            display: { xs: 'none', md: 'flex' }, 
            alignItems: 'center', 
            gap: 2 
          }}>
            {/* Einstellungen */}
            <Tooltip title="Einstellungen" arrow>
              <StyledIconButton onClick={handleSettings}>
                <SettingsOutlined />
              </StyledIconButton>
            </Tooltip>

            {/* Benutzer-Bereich */}
            <UserSection onClick={handleMenuOpen}>
              <Avatar 
                sx={{ 
                  width: 36, 
                  height: 36,
                  background: tritonStyles.gradients.accent,
                  color: '#ffffff',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {(user?.name || user?.username || 'U').charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#ffffff', 
                    fontWeight: 500,
                    fontSize: '0.875rem' 
                  }}
                >
                  {user?.name || user?.username || 'Benutzer'}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '0.75rem' 
                  }}
                >
                  {user?.role || 'Benutzer'}
                </Typography>
              </Box>
            </UserSection>
          </Box>
        </Toolbar>
      </TritonAppBar>

      {/* Benutzer-Menü */}
      <UserMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2d3748' }}>
            {user?.name || user?.username || 'Benutzer'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#4a5568' }}>
            {user?.email || ''}
          </Typography>
        </Box>
        
        <MenuItem onClick={handleOpenProfil} sx={{ py: 1.5 }}>
          <AccountCircleOutlined sx={{ mr: 2, color: '#4a5568' }} />
          <Typography variant="body2">Profil bearbeiten</Typography>
        </MenuItem>
        
        <MenuItem onClick={handleSettings} sx={{ py: 1.5 }}>
          <SettingsOutlined sx={{ mr: 2, color: '#4a5568' }} />
          <Typography variant="body2">Einstellungen</Typography>
        </MenuItem>

        <MenuItem onClick={handleTeamSettings} sx={{ py: 1.5 }}>
          <GroupOutlined sx={{ mr: 2, color: '#4a5568' }} />
          <Typography variant="body2">Team & Rechte</Typography>
        </MenuItem>

        <MenuItem onClick={handleBillingSettings} sx={{ py: 1.5 }}>
          <CreditCardOutlined sx={{ mr: 2, color: '#4a5568' }} />
          <Typography variant="body2">Abo & Features</Typography>
        </MenuItem>
        
        <Divider sx={{ my: 1 }} />
        
        <MenuItem 
          onClick={handleLogout} 
          sx={{ 
            py: 1.5,
            color: '#e53e3e',
            '&:hover': {
              backgroundColor: 'rgba(229, 62, 62, 0.08)',
            }
          }}
        >
          <LogoutOutlined sx={{ mr: 2 }} />
          <Typography variant="body2">Abmelden</Typography>
        </MenuItem>
      </UserMenu>

      {/* Spacer für Fixed AppBar */}
      <Toolbar sx={{ minHeight: { xs: 64, sm: 72 } }} />

      {/* ── Profil-Dialog ─────────────────────────────────────── */}
      <Dialog open={profilOpen} onClose={() => setProfilOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: '#1a365d', width: 40, height: 40, fontSize: '1.1rem' }}>
              {(user?.username || user?.name || 'U').charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1 }}>
                {user?.username || user?.name || 'Profil'}
              </Typography>
              <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
            </Box>
            {stammBefuellt && (
              <Chip icon={<CheckCircleOutline />} label="Stammdaten vollständig"
                size="small" color="success" variant="outlined" sx={{ ml: 'auto' }} />
            )}
          </Box>

          {/* Tab-Switcher */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            {['stamm', 'passwort'].map((tab) => (
              <Button key={tab} size="small"
                variant={profilTab === tab ? 'contained' : 'outlined'}
                onClick={() => { setProfilTab(tab); setProfilMsg({ text: '', error: false }); }}
                sx={profilTab === tab
                  ? { bgcolor: '#1a365d', '&:hover': { bgcolor: '#2d3748' } }
                  : { borderColor: '#cbd5e0', color: '#4a5568' }}
              >
                {tab === 'stamm' ? 'Stammdaten' : 'Passwort ändern'}
              </Button>
            ))}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {profilMsg.text && (
            <Alert severity={profilMsg.error ? 'error' : 'success'} sx={{ mb: 2 }}
              onClose={() => setProfilMsg({ text: '', error: false })}>
              {profilMsg.text}
            </Alert>
          )}

          {profilLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : profilTab === 'stamm' ? (
            <form id="stamm-form" onSubmit={handleStammSave}>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Kontakt</Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={8}>
                  <TextField fullWidth size="small" label="Firma / Name" value={stamm.firma}
                    onChange={(e) => setStamm(s => ({ ...s, firma: e.target.value }))}
                    InputProps={{ startAdornment: <InputAdornment position="start"><BusinessOutlined fontSize="small" /></InputAdornment> }} />
                </Grid>
                <Grid item xs={4}>
                  <TextField fullWidth size="small" label="Telefon" value={stamm.telefon}
                    onChange={(e) => setStamm(s => ({ ...s, telefon: e.target.value }))}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PhoneOutlined fontSize="small" /></InputAdornment> }} />
                </Grid>
              </Grid>

              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Anschrift</Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={8}>
                  <TextField fullWidth size="small" label="Straße" value={stamm.strasse}
                    onChange={(e) => setStamm(s => ({ ...s, strasse: e.target.value }))}
                    InputProps={{ startAdornment: <InputAdornment position="start"><HomeOutlined fontSize="small" /></InputAdornment> }} />
                </Grid>
                <Grid item xs={4}>
                  <TextField fullWidth size="small" label="Hausnummer" value={stamm.hausnummer}
                    onChange={(e) => setStamm(s => ({ ...s, hausnummer: e.target.value }))} />
                </Grid>
                <Grid item xs={4}>
                  <TextField fullWidth size="small" label="PLZ" value={stamm.plz}
                    onChange={(e) => setStamm(s => ({ ...s, plz: e.target.value }))}
                    inputProps={{ maxLength: 10 }} />
                </Grid>
                <Grid item xs={8}>
                  <TextField fullWidth size="small" label="Ort" value={stamm.ort}
                    onChange={(e) => setStamm(s => ({ ...s, ort: e.target.value }))} />
                </Grid>
              </Grid>

              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Bankverbindung</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" label="IBAN" value={stamm.iban}
                    onChange={(e) => setStamm(s => ({ ...s, iban: e.target.value.toUpperCase() }))}
                    inputProps={{ maxLength: 34 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><AccountBalanceOutlined fontSize="small" /></InputAdornment> }} />
                </Grid>
                <Grid item xs={3}>
                  <TextField fullWidth size="small" label="BIC" value={stamm.bic}
                    onChange={(e) => setStamm(s => ({ ...s, bic: e.target.value.toUpperCase() }))}
                    inputProps={{ maxLength: 11 }} />
                </Grid>
                <Grid item xs={3}>
                  <TextField fullWidth size="small" label="Bank" value={stamm.bank_name}
                    onChange={(e) => setStamm(s => ({ ...s, bank_name: e.target.value }))} />
                </Grid>
              </Grid>

              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 2, mb: 1 }}>
                Unterschrift (fuer Mietbescheinigung)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                {stamm.unterschrift_bild_url && (
                  <Box
                    component="img"
                    src={stamm.unterschrift_bild_url}
                    alt="Unterschrift"
                    sx={{ maxHeight: 50, maxWidth: 180, border: '1px solid #e2e8f0', borderRadius: 1, p: 0.5, bgcolor: '#fff' }}
                  />
                )}
                <Button
                  variant="outlined"
                  size="small"
                  component="label"
                  disabled={unterschriftUploading}
                  startIcon={unterschriftUploading ? <CircularProgress size={16} /> : null}
                  sx={{ borderColor: '#cbd5e0', color: '#4a5568' }}
                >
                  {stamm.unterschrift_bild_url ? 'Ersetzen' : 'Hochladen (PNG/JPG)'}
                  <input
                    type="file"
                    accept="image/png,image/jpeg"
                    hidden
                    onChange={(e) => {
                      const f = e.target.files[0];
                      if (f) handleUnterschriftUpload(f);
                    }}
                  />
                </Button>
                {stamm.unterschrift_bild_url && (
                  <Button size="small" color="error" variant="text"
                    onClick={() => setStamm(s => ({ ...s, unterschrift_bild_url: '' }))}>
                    Entfernen
                  </Button>
                )}
              </Box>
            </form>
          ) : (
            <form id="pw-form" onSubmit={handlePasswortSave}>
              <TextField fullWidth size="small" label="Aktuelles Passwort" type="password"
                value={pwAlt} onChange={(e) => setPwAlt(e.target.value)}
                sx={{ mb: 2 }} required
                InputProps={{ startAdornment: <InputAdornment position="start"><LockOutlined fontSize="small" /></InputAdornment> }} />
              <TextField fullWidth size="small" label="Neues Passwort" type="password"
                value={pwNeu} onChange={(e) => setPwNeu(e.target.value)}
                sx={{ mb: 2 }} required helperText="Mindestens 6 Zeichen"
                InputProps={{ startAdornment: <InputAdornment position="start"><LockOutlined fontSize="small" /></InputAdornment> }} />
              <TextField fullWidth size="small" label="Neues Passwort bestätigen" type="password"
                value={pwBest} onChange={(e) => setPwBest(e.target.value)}
                required
                InputProps={{ startAdornment: <InputAdornment position="start"><LockOutlined fontSize="small" /></InputAdornment> }} />
            </form>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setProfilOpen(false)} sx={{ color: '#4a5568' }}>Schließen</Button>
          <Button
            type="submit"
            form={profilTab === 'stamm' ? 'stamm-form' : 'pw-form'}
            variant="contained"
            disabled={profilSaving}
            startIcon={profilSaving ? <CircularProgress size={16} /> : <SaveOutlined />}
            sx={{ bgcolor: '#1a365d', '&:hover': { bgcolor: '#2d3748' } }}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TritonHeader;
